/*
 * ===============================================================================================================
 *                                Copyright 2021, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */

import * as React from 'react';
import styled from '@emotion/styled';
import { Box, Checkbox, List, ListItemButton, ListItemText, Collapse, Typography } from '@mui/material';
import _ from 'lodash';
import { LuiTreeProps, MultiSelectItem, CheckStatus, NodeItem } from './types';
import { LightTheme } from '@jda/lui-common-component-library-mui5';
import ArrowChevronDown from '@jda/lui-common-icon-library-mui5/ui/ArrowChevronDown';
import ArrowChevronRight from '@jda/lui-common-icon-library-mui5/ui/ArrowChevronRight';
import { Fragment } from 'react';

const { spacing } = LightTheme;

const Item = styled(ListItemButton)`
  padding-top: 0px;
  padding-bottom: 0px;
  cursor:pointer,
  height: ${spacing(6)};
`;
const ArrowDown = styled(ArrowChevronDown)`
  && {
    font-size: ${spacing(2.25)};
  }
`;

const ArrowRight = styled(ArrowChevronRight)`
  && {
    font-size: ${spacing(2.25)};
  }
`;
const LuiTree: React.FC<LuiTreeProps> = ({
  data,
  keyAccessor = 'key',
  displayAccessor = 'name',
  onSelectItem,
  selectedItems,
  width,
  className,
  showSelection = true,
  handleItemClick,
  expandedItems
}) => {
  const notifyParents = React.useCallback((item: NodeItem) => {
    if (item.parent) {
      const childrenLength = item.parent.children!.length;
      const selected = item.parent.children!.filter(child => child.selected);
      const hasIndeterminate = item.parent.children!.some(child => child.checkStatus === CheckStatus.INDETERMINATE);
      if (childrenLength === selected.length) {
        item.parent.selected = true;
        item.parent.checkStatus = CheckStatus.CHECKED;
      } else if (selected.length) {
        item.parent.selected = false;
        item.parent.checkStatus = CheckStatus.INDETERMINATE;
      } else {
        item.parent.selected = false;
        item.parent.checkStatus = hasIndeterminate ? CheckStatus.INDETERMINATE : CheckStatus.UNCHECKED;
      }
      notifyParents(item.parent);
    }
  }, []);

  const selectDescendants = React.useCallback((item: NodeItem) => {
    if (item.children && item.children.length > 0) {
      item.children.forEach(child => {
        child.selected = true;
        child.checkStatus = CheckStatus.CHECKED;
        if (child.children && child.children.length > 0) {
          selectDescendants(child);
        }
      });
    }
  }, []);

  const deselectDescendants = React.useCallback((item: NodeItem) => {
    if (item.children && item.children.length > 0) {
      item.children.forEach(child => {
        child.selected = false;
        child.checkStatus = CheckStatus.UNCHECKED;
        if (child.children && child.children.length > 0) {
          deselectDescendants(child);
        }
      });
    }
  }, []);

  const itemRenderer = React.useCallback(
    (item: NodeItem, index: number, level: number = 0, tree: NodeItem[]) => {
      let hasChildren = (item.children && item.children.length > 0);
      if (selectedItems.length) {
        // if keys are selected then first item should be selected
        if ((index === 0 && level === 0)) {
          item.checkStatus = CheckStatus.INDETERMINATE;
          item.allowExpandItem = true;
          hasChildren = true
        }
        // selected Hierarchy keys array ['1-51-13','3-45-28','1-1']
        selectedItems.forEach((selectedKey: any) => {
          // selected keys will be split here and mapped to level of item hierarchy and selcted 
          // as dropdown menu, for matching keys(hierarchy)
          const selctedKeyArray = selectedKey.split('-');
          // ['1','51','13']
          selctedKeyArray.forEach((currentKey: any, levelOfSelectedKey: number) => {
            let currentItemkey = currentKey;
            // matching selcted key's level based on level key from response of hierarchy
            const levelOfSelectedKeyIndex = 'productGroupLevel' + (levelOfSelectedKey + 1);
            if (item.level === levelOfSelectedKeyIndex) {
              if (levelOfSelectedKey > 0) {
                currentItemkey = selctedKeyArray.slice(0, (levelOfSelectedKey + 1)).join("-");
              }
              if (item.key === currentItemkey) {
                item.checkStatus = CheckStatus.CHECKED;
                item.allowExpandItem = false;
                // checking if last hierarchy level then item should not be selected
                if (!(selctedKeyArray.length === (levelOfSelectedKey + 1))) {
                  item.checkStatus = CheckStatus.INDETERMINATE;
                  item.allowExpandItem = true;
                  hasChildren = true;
                }
              }
            }
          })
        })

      }
      const itemWithChild = level > 0 ? level + 1.75 : level;
      const noChildrenItem = level > 0 ? level + 4 : level;

      const stopPropagation = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.stopPropagation();
      };

      const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
          item.selected = true;
          item.checkStatus = CheckStatus.CHECKED;
          notifyParents(item);
          selectDescendants(item);
        } else {
          item.selected = false;
          item.checkStatus = CheckStatus.UNCHECKED;
          notifyParents(item);
          deselectDescendants(item);
        }
        onSelectItem(item, tree);
      };

      const arrowIconState = expandedItems.includes(item[keyAccessor]) ? <ArrowDown /> : <ArrowRight />;

      return (
        <React.Fragment key={`item-${item[keyAccessor]}-${index}`}>
          <Item
            onClick={event => handleItemClick(event, item)}
            style={
              !hasChildren
                ? {
                  paddingLeft: spacing(noChildrenItem)
                }
                : { paddingLeft: spacing(itemWithChild) }
            }
          >
            {showSelection && (
              <Fragment>
                {hasChildren && arrowIconState}
                <Checkbox
                  checked={item.checkStatus === CheckStatus.CHECKED}
                  indeterminate={item.checkStatus === CheckStatus.INDETERMINATE}
                  color="primary"
                  onClick={stopPropagation}
                  onChange={handleChange}
                />
              </Fragment>
            )}
            <ListItemText disableTypography>
              <Typography variant="h4">
                <Box fontWeight="fontWeightRegular">{item[displayAccessor]}</Box>
              </Typography>
            </ListItemText>
          </Item>
          {hasChildren && (
            <Collapse in={expandedItems.includes(item[keyAccessor])} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {item.children && item.children!.map((nestedItem: any, nestedIndex: number) =>
                  itemRenderer(nestedItem, nestedIndex, level + 1, tree)
                )}
              </List>
            </Collapse>
          )}
        </React.Fragment>
      );
    },
    [
      keyAccessor,
      showSelection,
      displayAccessor,
      expandedItems,
      onSelectItem,
      notifyParents,
      selectDescendants,
      deselectDescendants,
      handleItemClick,
      selectedItems
    ]
  );

  function dataToItems(data: MultiSelectItem[], parentRef?: NodeItem): NodeItem[] {
    const tree = _.cloneDeep(data);
    tree.forEach(node => {
      node.checkStatus = CheckStatus.UNCHECKED;
      node.selected = false;
      node.parent = parentRef;
      if (node.children && node.children.length > 0) {
        node.children = dataToItems(node.children, node as NodeItem);
      }
    });
    return tree as NodeItem[];
  }

  function updateSelectionsInTree(tree: NodeItem[]) {
    const searchNode = (key: string, dataSet: NodeItem[]): NodeItem | undefined => {
      let foundNode: NodeItem | undefined = undefined;
      const findNode = (key: string, dataSet: NodeItem[]): NodeItem | undefined => {
        return dataSet.find(node => {
          if (node[keyAccessor] === key) {
            foundNode = node;
            return node;
          } else if (node.children?.length) {
            return findNode(key, node.children);
          }
          return undefined;
        });
      };
      findNode(key, dataSet);
      return foundNode;
    };

    selectedItems.forEach(key => {
      const node = searchNode(key, tree as NodeItem[]);
      if (node) {
        node.selected = true;
        node.checkStatus = CheckStatus.CHECKED;
        notifyParents(node);
      }
    });
  }

  const tree = dataToItems(data);
  updateSelectionsInTree(tree);

  return (
    <Box width={width} className={className}>
      <List component="div">{tree.map((item, index, tree) => itemRenderer(item, index, 0, tree))}</List>
    </Box>
  );
};

export default LuiTree;
